import type { LucideProps } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import AnimatedLynkIcon from 'src/features/lynk/components/animated-lynk-icon'
import NotificationCountBubble from 'src/features/notifications/components/ui/notification-count-bubble'
import { useNotificationModals } from 'src/features/notifications/context/notification-modals-context'
import AllNotificationsModal from 'src/features/notifications/modals/all-notifications-modal'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { useSignOut } from 'src/shared/features/auth/api'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import ProfileAvatar from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { useIsSubscribed } from 'src/shared/hooks/use-is-subscribed'

import ProfessionalAppFrameSearchBar from '../../search/components/pro-app-frame-search-bar'
import { ActivityIcon } from '../icons/activity-icon'
import { BusinessIcon } from '../icons/business-icon'
import { CommunityIcon } from '../icons/community-icon'
import { HelpCenterIcon } from '../icons/help-center-icon'
import { MessagesIcon } from '../icons/messages-icon'
import { PlusIcon } from '../icons/plus-icon'
import { SettingsIcon } from '../icons/settings-icon'

import TabBarButton from './pro-app-frame-tab-button'
import TabIconButton from './pro-app-frame-tab-icon-button'

const LEFT_TAB_BAR_BUTTONS = [
  {
    title: 'Community',
    key: 'community',
    path: paths.feed.path,
    icon: (props: LucideProps) => <CommunityIcon size={18} {...props} />,
  },
  {
    title: 'Business',
    key: 'business',
    path: paths.referrals.list.path,
    icon: (props: LucideProps) => <BusinessIcon size={18} {...props} />,
  },
  {
    title: 'Messages',
    key: 'messages',
    path: paths.chats.list.path,
    icon: (props: LucideProps) => <MessagesIcon size={18} {...props} />,
  },
]

const ProfessionalAppFrameHeader = () => {
  const { showNotificationModal, setShowNotificationModal } = useNotificationModals()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: currentUser } = useMyUserData()
  const isSubscribed = useIsSubscribed()
  const signOut = useSignOut()

  return (
    <div id="header" className="flex h-[70px] flex-row items-center justify-between">
      <div className="flex shrink-0 flex-row items-center gap-5">
        <Link to={paths.home.getHref()} className="ml-[15px] flex shrink-0 items-center justify-center">
          <Iconmark className="size-[28px] text-white md:size-[30px] xl:size-[35px]" />
        </Link>
        <div className="flex flex-row items-center gap-2.5">
          {LEFT_TAB_BAR_BUTTONS.map((button) => (
            <TabBarButton
              key={button.key}
              icon={button.icon}
              title={button.title}
              onClick={() => navigate(button.path)}
              isActive={location.pathname.startsWith(button.path)}
            />
          ))}
        </div>
      </div>
      <div className="flex shrink-0 flex-row items-center gap-2.5">
        <button
          onClick={() => navigate(paths.business.create.getHref('1'))}
          className="flex shrink-0 items-center justify-center"
        >
          <div className="flex size-[45px] cursor-pointer items-center justify-center rounded-full bg-white transition-all duration-200 hover:bg-secondaryInverse">
            <PlusIcon className="size-[18px] text-primary" />
          </div>
        </button>
        <TabIconButton
          key="lynk-workflows"
          icon={() => <AnimatedLynkIcon variant="gradient" autoPlay={isSubscribed} width={36} height={36} />}
          onClick={() => navigate(paths.settings.path)}
          isActive={location.pathname.startsWith(paths.settings.path)}
        />
        <ProfessionalAppFrameSearchBar />
        <TabIconButton
          key="settings"
          icon={(props: LucideProps) => <SettingsIcon size={22} {...props} />}
          onClick={() => navigate(paths.settings.path)}
          isActive={location.pathname.startsWith(paths.settings.path)}
        />
        <AllNotificationsModal
          open={showNotificationModal}
          trigger={
            <div className="relative">
              <TabIconButton
                key="activity"
                onClick={() => {}}
                icon={(props: LucideProps) => <ActivityIcon size={22} {...props} />}
                isActive={showNotificationModal}
              />
              <NotificationCountBubble />
            </div>
          }
          onOpenChange={setShowNotificationModal}
        />
        <TabIconButton
          key="help-center"
          icon={(props: LucideProps) => <HelpCenterIcon size={22} {...props} />}
          onClick={() => navigate(paths.support.path)}
          isActive={location.pathname.startsWith(paths.support.path)}
        />
        <div
          className="flex shrink-0 cursor-pointer flex-col items-center gap-3"
          onClick={() => {
            // navigate(paths.users.profile.getHref(currentUser?.username ?? ''))
            signOut.mutateAsync()
          }}
        >
          <ProfileAvatar
            size={39}
            userId={currentUser?.id ?? -1}
            className="box-content shrink-0 border-[3px] border-white"
          />
        </div>
      </div>
    </div>
  )
}

export default ProfessionalAppFrameHeader
